<template>
    <pendo-filter-bar
        v-pendo-affix="affixStyles"
        class="filter-bar">
        <div
            v-if="loading"
            class="filter-bar__loading">
            <pendo-loading-indicator size="medium" />
        </div>
        <div
            v-else
            class="filter-bar__top">
            <fieldset class="filter-bar__left">
                <legend class="sr-only">
                    Global filters
                </legend>
                <div class="filter-bar__left-prepended">
                    <slot name="prependedLeftFilters" />
                </div>
                <segment-chooser
                    v-if="showHideConfig.segmentChooser"
                    :value="segment"
                    :aria-label="$t('globalFilterBar.Filter by segment')"
                    :max-trigger-width="220"
                    :allow-create-new-segment="true"
                    :guides-only="segmentsGuidesOnly"
                    :exclude-anon-default="segmentsExcludeAnonDefault"
                    :theme="theme"
                    @input="updateSegment" />
                <pendo-divider
                    v-if="showHideConfig.allowAllTime"
                    direction="vertical"
                    width="8px"
                    height="36px" />
                <div
                    v-if="showHideConfig.allowAllTime"
                    class="pendo-input__label pendo-input__label--top filter-bar__all-time-label">
                    All Time
                </div>
                <pendo-toggle
                    v-if="allTimeLoaded && showHideConfig.allowAllTime"
                    class="filter-bar__all-time-toggle"
                    data-cy="all-time-toggle"
                    :value="allTimeToggle"
                    @change="updateAllTime" />
                <time-series-picker
                    v-if="showTimeSeriesPicker"
                    :aria-label="$t('globalFilterBar.Filter by date range')"
                    :time-series="globalTimeSeries"
                    :shortcuts="timeSeriesShortcuts"
                    @change="updateTimeSeries">
                    <template #value="{ value }">
                        <slot
                            name="timeSeriesPickerValue"
                            :value="value" />
                    </template>
                </time-series-picker>
                <pendo-divider
                    v-if="showDivider"
                    direction="vertical"
                    width="10px"
                    height="36px" />
                <account-chooser
                    v-if="showHideConfig.accountChooser"
                    :aria-label="$t('globalFilterBar.Filter by account')"
                    :account="account"
                    :loading="!account"
                    @select-account="updateAccount" />
                <multi-app-chooser
                    v-if="showAppChooser"
                    :loading="!apps"
                    :value="apps"
                    :aria-label="$t('globalFilterBar.Filter by application')"
                    :disabled="appChooserDisabled"
                    :enabled-map="enabledMapForAppChooser"
                    :exclude-groups="excludeGroupsForAppChooser"
                    :filter-non-integrated-apps="filterNonIntegratedApps"
                    @apply="updateApps" />
                <single-app-chooser
                    v-if="showSingleAppChooser"
                    :aria-label="$t('globalFilterBar.Filter by application')"
                    :app-options-config="appChooserOptionsConfig"
                    :loading="!apps"
                    :selected-app="singleAppChooserSelected"
                    @set-application="updateAppsSingleSelect" />
                <language-filter
                    v-if="showHideConfig.languageChooser"
                    :app-id="appId"
                    :aria-label="$t('globalFilterBar.Filter by language')"
                    :value="globalSelectedLanguage"
                    :config="config.languageChooser"
                    @change="updateLanguage" />
                <feature-filter v-if="showFeatureFilter" />
                <verification-filter
                    v-if="showHideConfig.verification"
                    :aria-label="$t('globalFilterBar.Filter by verification status')"
                    :value="globalVerification"
                    @input="updateVerification" />
                <star-filter
                    v-if="showHideConfig.starred"
                    data-cy="star-filter"
                    :value="!!starred"
                    @change="updateStarred" />
                <slot
                    name="additional-left-filters"
                    :update-time-series="updateTimeSeries" />
            </fieldset>
            <div class="filter-bar__right">
                <slot
                    name="right"
                    :update-time-series="updateTimeSeries" />
                <ask-pendo-context-button
                    v-if="canAccessAgentMode"
                    :context="viewContext"
                    :countable-type="$route.meta.agentModeCountableType"
                    :countable-id="agentModeCountableId"
                    :countable-name="agentModeCountableName"
                    :suggested-questions="suggestedQuestions"
                    :disabled="!agentModeCountableLoaded"
                    class="page-alert"
                    @open-conversation-drawer="onAskPendoDrawerOpen"
                    @close-conversation-drawer="onAskPendoDrawerClose" />
            </div>
        </div>
        <template #bottom>
            <div
                v-if="!!$slots.expansion"
                class="filter-bar__bottom">
                <slot name="expansion" />
            </div>
        </template>
    </pendo-filter-bar>
</template>

<script>
import { PendoAffix, PendoDivider, PendoFilterBar, PendoLoadingIndicator, PendoToggle } from '@pendo/components';
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import { computed } from 'vue';
import SingleAppChooser from '@/dashboards-v2/templates/template-settings/common/single-app-chooser.vue';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import { isEqualArrayContent } from '@/utils/lodash.utils';
import SegmentChooser from '@/common/segment-chooser/segment-chooser.vue';
import TimeSeriesPicker from '../time-series-picker/time-series-picker.vue';
import AccountChooser from '../account-chooser/account-chooser.vue';
import LanguageFilter from '@/common/language-filter/language-filter.vue';
import VerificationFilter from '@/common/verification-status/verification-filter.vue';
import MultiAppChooser from '@/common/multi-app-chooser/multi-app-chooser.vue';
import StarFilter from './filters/star-filter/star-filter.vue';
import FeatureFilter from '@/features/feature-filter/feature-filter.vue';
import { isParentAccount } from '@/common/account-chooser/util';
import { getAppDisplayName, isPlatformApp } from '@/apps/apps.utils';
import { PerSubscriptionUserStorage } from '@/utils/storage';
import { useFixedStyles } from '@/common/use/use-fixed-styles/use-fixed-styles';
import { useBreakpoints } from '@vueuse/core';
import { BREAKPOINTS } from '@pendo/less-variables/lib/utils/breakpoints';
import { PRODUCT_LINES } from '@/settings/subscription-settings/product-lines';
import AskPendoContextButton from '@/ask-pendo/components/ask-pendo-context-button.vue';
import { buildViewContext } from '@/ask-pendo/utils/agent-mode-context-builders';

export default {
    name: 'FilterBar',
    components: {
        MultiAppChooser,
        PendoFilterBar,
        AccountChooser,
        SegmentChooser,
        TimeSeriesPicker,
        StarFilter,
        LanguageFilter,
        PendoToggle,
        PendoDivider,
        PendoLoadingIndicator,
        FeatureFilter,
        VerificationFilter,
        SingleAppChooser,
        AskPendoContextButton,
    },
    directives: {
        PendoAffix,
    },
    props: {
        filtersShowHideConfig: {
            type: Object,
            default: () => ({}),
        },
        filtersConfig: {
            type: Object,
            default: () => ({}),
        },
        timeSeriesShortcuts: {
            type: Array,
            default: undefined,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        agentModeCountableId: {
            type: String,
            required: false,
            default: '',
        },
        agentModeCountableName: {
            type: String,
            required: false,
            default: '',
        },
        agentModeCountableLoaded: {
            type: Boolean,
            default: false,
        },
        suggestedQuestions: {
            type: Array,
            default: () => [],
        },
    },
    emits: ['update-apps-filter'],
    setup () {
        const { affixStyles: baseAffixStyles } = useFixedStyles();
        const breakpoints = useBreakpoints({ medium: BREAKPOINTS.MEDIUM });
        const shouldAffix = breakpoints.greater('medium');

        const affixStyles = computed(() => ({
            ...baseAffixStyles.value,
            shouldAffix: shouldAffix.value,
        }));

        return {
            affixStyles,
        };
    },
    data () {
        return {
            segment: undefined,
            account: undefined,
            apps: undefined,
            starred: undefined,
            theme: 'light',
            config: {
                languageChooser: {
                    resourceCenter: undefined,
                },
                filterType: null,
            },
            allTimeToggle: true,
            allTimeLoaded: false,
            navExpandedBeforeDrawer: null,
        };
    },
    computed: {
        ...mapGetters('segments', ['segmentById']),
        ...mapGetters('auth', ['hasSegmentFlag']),
        ...mapGetters('subscription', ['getFeatureFlag']),
        ...mapGetters({
            getAppById: 'apps/getAppById',
            isMultiApp: 'apps/isMultiApp',
            hasAccountLevelAnalyticsEnabled: 'subscription/hasAccountLevelAnalyticsEnabled',
        }),
        ...mapGetters('globalFilters', ['starredByType']),
        ...mapState({
            guide: (state) => state.guides.guide,
            globalSegmentId: (state) => state.globalFilters.selectedSegmentId,
            globalTimeSeries: (state) => state.globalFilters.timeSeries,
            globalSelectedAccount: (state) => state.globalFilters.selectedAccount,
            globalSelectedApps: (state) => state.globalFilters.selectedApps,
            globalSelectedLanguage: (state) => state.globalFilters.selectedLanguage,
            globalStarred: (state) => state.globalFilters.starred,
            globalVerification: (state) => state.globalFilters.selectedVerification,
            globalAllTime: (state) => state.globalFilters.allTime,
        }),
        ...mapState('leftNav', ['expanded']),
        canAccessAgentMode () {
            return this.$route?.meta?.agentModeCountableType && this.hasSegmentFlag('askPendo') && this.getFeatureFlag('askPendo');
        },
        viewContext () {
            if (!this.canAccessAgentMode) {
                return {};
            }

            return buildViewContext({
                viewType: this.$route.meta.agentModeCountableType,
                viewId: this.agentModeCountableId,
                viewName: this.agentModeCountableName,
            });
        },
        showHideConfig () {
            return {
                accountChooser: this.showAccounts,
                timeSeries: true,
                appChooser: true,
                singleAppChooser: false,
                segmentChooser: true,
                starred: false,
                languageChooser: false,
                allowAllTime: false,
                ...this.filtersShowHideConfig,
            };
        },
        appId () {
            return (
                get(this.guide, 'appId') || get(this.config, 'languageChooser.resourceCenter.public.modules[0].appId')
            );
        },
        appChooserOptionsConfig () {
            return get(this.filtersConfig, 'appChooser.appOptionsConfig', undefined);
        },
        excludeGroupsForAppChooser () {
            return get(this.config, 'appChooser.excludeGroups', false);
        },
        appChooserDisabled () {
            return get(this.filtersConfig, 'appChooser.disabled', false);
        },
        enabledMapForAppChooser () {
            return get(this.config, 'appChooser.enabledMap', null);
        },
        filterNonIntegratedApps () {
            return get(this.config, 'appChooser.filterNonIntegratedApps', false);
        },
        segmentsGuidesOnly () {
            return get(this.config, 'segmentChooser.guidesOnly', false);
        },
        segmentsExcludeAnonDefault () {
            return get(this.config, 'segmentChooser.excludeAnonDefault', false);
        },
        showFeatureFilter () {
            return get(this.config, 'filterType', '') === 'feature';
        },
        showDivider () {
            return (
                this.showHideConfig.allowAllTime &&
                (this.showHideConfig.accountChooser ||
                    (this.isMultiApp && this.showHideConfig.appChooser) ||
                    this.showHideConfig.starred ||
                    this.showHideConfig.languageChooser)
            );
        },
        showTimeSeriesPicker () {
            return (
                this.showHideConfig.timeSeries &&
                (!this.showHideConfig.allowAllTime || (this.showHideConfig.allowAllTime && !this.allTimeToggle))
            );
        },
        showAppChooser () {
            return this.isMultiApp && this.showHideConfig.appChooser && !this.showHideConfig.singleAppChooser;
        },
        showSingleAppChooser () {
            return this.isMultiApp && this.showHideConfig.singleAppChooser && !this.showHideConfig.appChooser;
        },
        singleAppChooserSelected () {
            return this.apps ? this.apps[0] : {};
        },
        showAccounts () {
            return this.hasAccountLevelAnalyticsEnabled && this.$productLine(PRODUCT_LINES.CORE, 'accountsUIEnabled');
        },
    },
    watch: {
        globalSegmentId () {
            this.setSegmentFromGlobal();
        },
        globalSelectedAccount: {
            deep: true,
            handler () {
                if (this.showAccounts) {
                    this.setAccountFromGlobal();
                }
            },
        },
        globalSelectedApps: {
            deep: true,
            handler () {
                this.setAppsFromGlobal();
            },
        },
        globalAllTime () {
            this.setAllTimeFromGlobal();
        },
    },
    activated () {
        this.fetchAndSetData();
    },
    created () {
        this.config = Object.assign(this.config, this.filtersConfig);
        this.fetchAndSetData();
    },
    methods: {
        ...mapActions('segments', { loadAllSegments: 'loadAll' }),
        ...mapActions('globalFilters', [
            'loadSelectedAccount',
            'loadSelectedChannel',
            'loadSelectedApps',
            'loadSelectedStarred',
            'loadSelectedLanguage',
            'loadVerification',
            'loadAllTime',
            'updateSelectedTimeSeries',
        ]),
        ...mapActions('leftNav', { expandCollapseNav: 'expandCollapseNav' }),
        ...mapMutations('globalFilters', [
            'setSelectedSegmentId',
            'setSelectedAccount',
            'setSelectedApps',
            'setSelectedLanguage',
            'setStarred',
            'setVerification',
            'setAllTime',
        ]),
        onAskPendoDrawerOpen () {
            this.navExpandedBeforeDrawer = this.expanded;
            this.expandCollapseNav({ expanded: false });
        },
        onAskPendoDrawerClose () {
            if (this.navExpandedBeforeDrawer !== null) {
                this.expandCollapseNav({ expanded: this.navExpandedBeforeDrawer });
                this.navExpandedBeforeDrawer = null;
            }
        },
        updateSegment (segment) {
            this.setSelectedSegmentId({
                selectedSegmentId: segment.id,
                updateAngular: true,
            });

            this.setSegmentFromGlobal();
            window.pendo?.track('filters', {
                filterType: 'segment',
            });
        },
        updateTimeSeries (timeSeries) {
            this.updateSelectedTimeSeries({
                timeSeries,
                fromVue: true,
            });
            window.pendo?.track('filters', {
                filterType: 'time-series',
            });
        },
        updateAccount (account) {
            if (!account) {
                return;
            }

            const _account = { ...account };
            if (account.isParentAccount == null) {
                _account.isParentAccount = isParentAccount(account);
            }

            this.setSelectedAccount({
                selectedAccount: {
                    account: _account,
                },
                updateAngular: true,
            });
            this.setAccountFromGlobal();
            window.pendo?.track('filters', {
                filterType: 'account-chooser',
            });
        },
        updateAppsSingleSelect (appId) {
            this.updateApps([appId.id]);
            window.pendo?.track('filters', {
                filterType: 'app-chooser',
            });
        },
        updateApps (appIds) {
            if (isEqualArrayContent(this.apps, appIds)) return;

            const newSelectedApps = isPlatformApp(appIds[0])
                ? [this.platformAppOption(appIds[0])]
                : appIds.map((appId) => this.getAppById(appId));

            this.setSelectedApps({
                selectedApps: newSelectedApps,
                updateAngular: true,
            });
            this.setAppsFromGlobal();
            this.$emit('update-apps-filter', appIds);
            window.pendo?.track('filters', {
                filterType: 'app-chooser',
            });
        },
        updateLanguage ({ code }) {
            this.setSelectedLanguage({ selectedLanguage: code });
            PerSubscriptionUserStorage.set('selectedLanguage', code);
        },
        updateVerification (value) {
            this.setVerification({ verification: value, persist: true });
        },
        updateStarred (starred) {
            const { filterType } = this.config;
            this.setStarred({ starred, filterType, updateAngular: true });
            this.setStarredFromGlobal();
            window.pendo?.track('filters', {
                filterType: 'starred',
            });
        },
        updateAllTime (value) {
            this.allTimeToggle = value;
            this.setAllTime({ allTime: value });
            window.pendo?.track('filters', {
                filterType: 'all-time-toggle',
            });
        },
        setSegmentFromGlobal () {
            this.segment = cloneDeep(this.segmentById(this.globalSegmentId));
        },
        setAccountFromGlobal () {
            this.account = cloneDeep(this.globalSelectedAccount.account);
        },
        setAppsFromGlobal () {
            this.apps = cloneDeep(this.globalSelectedApps);
        },
        setStarredFromGlobal () {
            if (this.config.filterType) {
                this.starred = this.starredByType(this.config.filterType);
            } else {
                this.starred = cloneDeep(this.globalStarred);
            }
        },
        setAllTimeFromGlobal () {
            this.allTimeToggle = this.globalAllTime;
        },
        platformAppOption (appId) {
            return {
                displayName: getAppDisplayName(appId),
                id: appId,
            };
        },
        async fetchAndSetData () {
            await Promise.all([
                this.loadAllSegments(),
                this.loadSelectedChannel(),
                this.loadSelectedApps(),
                this.loadSelectedStarred(),
                this.loadSelectedLanguage(),
                this.loadVerification(),
                this.loadAllTime(),
            ]);
            if (this.showAccounts) {
                await this.loadSelectedAccount();
                this.setAccountFromGlobal();
            }
            this.setSegmentFromGlobal();
            this.setAppsFromGlobal();
            this.setStarredFromGlobal();
            this.setAllTimeFromGlobal();
            this.allTimeLoaded = true;
        },
    },
};
</script>

<style scoped lang="less">
.pendo-filter-bar {
    padding: 0;
}

.filter-bar {
    &__loading,
    &__top {
        height: auto;
        display: flex;
        width: 100%;
        align-items: flex-start;
        padding: 12px 16px;

        .screen-size(@zero, @medium, {
            flex-direction: column;
            gap: 4px;

            .filter-bar__right {
                margin: 0;
            }

            .filter-bar__left {
                flex-direction: column;
                margin: 0px;

                & > *:not(:empty) {
                    margin: 4px 0px;
                }
            }
        });
    }
    &__loading {
        align-items: center;
        justify-content: center;
    }
}

fieldset {
    width: 100%;
}

.filter-bar__left {
    display: flex;
    flex-wrap: wrap;
    align-content: flex-start;
    margin: -4px;

    > *:not(:empty) {
        margin: 4px 8px;
    }

    span {
        align-self: center;
    }

    .filter-bar__all-time-toggle {
        margin-left: 0px;
        display: flex;
        padding: 8px 0 0 0;

        :deep(.pendo-toggle) {
            margin: 4px 0px;
        }
    }
}

.filter-bar__right {
    margin-left: auto;
    display: flex;
    gap: 8px;
}

.filter-bar__bottom {
    padding: 12px 16px;
    display: flex;
    flex-wrap: wrap;
    align-content: flex-start;
    margin: -4px;

    :deep(.pendo-multiselect) {
        margin: 4px 8px;
    }
}

.filter-bar__all-time-label {
    padding: 12px 0 0 0;
}
</style>
